import { BellRing, Building2, FolderGit2, Layers, Pencil, Plus, Trash2, Users } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { ALL_PROJECTS_WORKSPACE_ID, TEAMS_NOTIFY_EVENTS, TEAMS_NOTIFY_EVENT_LABEL } from '@orgus/protocol';
import type { MemberPrefs, Team, TeamsState, Urgency, Workspace } from '@orgus/protocol';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Panel,
  RelativeTime,
  Select,
  SkeletonRows,
  toast,
} from '@orgus/ui';
import type { Column } from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import { useOrgus } from '../../lib/store.ts';
import { InlineText } from '../detail/InlineEdit.tsx';
import { useCan } from '../auth/useMe.ts';
import { useResource } from '../auth/useResource.ts';
import { CheckList } from './CheckList.tsx';
import { teamsClient } from './client.ts';
import { personOf, useMyLogin } from './model.ts';
import { AvatarStack } from './PresenceAvatars.tsx';
import { TeamsGate } from './TeamsGate.tsx';
import { useWorkspaceChoice } from './WorkspaceSwitcher.tsx';

async function attempt(fn: () => Promise<unknown>, done: string, detail?: string): Promise<boolean> {
  try {
    await fn();
    toast.success(done, detail);
    return true;
  } catch (e) {
    toast.error(e);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Organisation
// ---------------------------------------------------------------------------

function OrgPanel({ data, canManage }: { data: TeamsState; canManage: boolean }) {
  const org = data.orgs[0]!;
  const rename = async (name: string) => {
    if (!(await attempt(() => teamsClient.renameOrg(name), 'Organisation renamed'))) throw new Error('not renamed');
  };
  return (
    <Panel title="Organisation" icon={<Building2 />}>
      {/* The name reads as a value and becomes a text box on click; the id is a quiet fact beside it. */}
      <dl className="grid max-w-md grid-cols-2 gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <dt className="text-caption text-fg-subtle">Name</dt>
          <dd className="min-w-0">
            {canManage ? (
              <InlineText
                label="Organisation name"
                value={org.name}
                empty="Add a name"
                validate={(t) => (!t ? 'Give the organisation a name' : t.length > 60 ? 'Use 60 characters or fewer' : null)}
                onCommit={rename}
                width="w-full"
                className="-ml-2 max-w-full text-fg"
              />
            ) : (
              <span className="block truncate text-ui text-fg">{org.name}</span>
            )}
          </dd>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <dt className="text-caption text-fg-subtle">Id</dt>
          <dd className="flex h-control-sm min-w-0 items-center truncate font-mono text-caption text-fg-muted">{org.id}</dd>
        </div>
      </dl>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

function WorkspaceDrawer({ ws, open, onClose }: { ws: Workspace | null; open: boolean; onClose: () => void }) {
  const projects = useOrgus((s) => s.full.projects);
  const [name, setName] = useState('');
  const [ids, setIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setName(ws?.name ?? '');
    setIds(ws?.projectIds ?? []);
    setError(null);
  }, [open, ws]);
  const options = useMemo(
    () =>
      Object.values(projects)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((p) => ({ value: p.id, label: p.name, detail: p.remoteUrl?.replace(/^.*[:/]([^/]+\/[^/]+?)(\.git)?$/, '$1') ?? 'local only', leading: <FolderGit2 className="size-3.5 text-fg-subtle" aria-hidden /> })),
    [projects],
  );
  const save = async () => {
    if (!name.trim()) {
      setError('Give the workspace a name, for example Payments.');
      return;
    }
    setBusy(true);
    const ok = await attempt(
      () => (ws ? teamsClient.updateWorkspace(ws.id, { name: name.trim(), projectIds: ids }) : teamsClient.createWorkspace(name.trim(), ids)),
      ws ? `${name.trim()} saved` : `${name.trim()} created`,
      `${ids.length} ${ids.length === 1 ? 'project' : 'projects'}. Pick it from the switcher in the top bar.`,
    );
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Drawer
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={ws ? `Edit ${ws.name}` : 'New workspace'}
      description="A workspace shows only its projects on every page. Machines stay shared."
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" className="ml-auto" loading={busy} onClick={() => void save()}>
            {ws ? 'Save workspace' : 'Create workspace'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 p-5">
        <Input label="Name" value={name} onChange={(e) => (setName(e.target.value), setError(null))} error={error ?? undefined} maxLength={60} autoFocus />
        <CheckList label="Projects" options={options} value={ids} onChange={setIds} emptyText="No projects yet. Add one in Projects first." />
      </div>
    </Drawer>
  );
}

function WorkspacesPanel({ data, canManage }: { data: TeamsState; canManage: boolean }) {
  const projects = useOrgus((s) => s.full.projects);
  const current = useWorkspaceChoice((s) => s.id);
  const [editing, setEditing] = useState<Workspace | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<Workspace | null>(null);
  const narrow = useMediaQuery(NARROW_QUERY);
  const projectsOf = (w: Workspace) =>
    w.projectIds === null ? 'Every project' : w.projectIds.length === 0 ? 'None yet' : w.projectIds.map((id) => projects[id]?.name ?? 'Unknown project').join(', ');
  const columns: Column<Workspace>[] = [
    {
      key: 'name',
      header: 'Workspace',
      grow: true,
      text: (w) => w.name,
      sortValue: (w) => (w.builtIn ? '' : w.name.toLowerCase()),
      cell: (w) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Layers className="size-4 shrink-0 text-fg-subtle" aria-hidden />
          <span className="flex min-w-0 flex-col">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate font-medium text-fg">{w.name}</span>
              {/* One chip per row; built in reads in the Created column (or the phone meta line). */}
              {w.id === current && (
                <Badge tone="accent" className="shrink-0">
                  Showing
                </Badge>
              )}
            </span>
            {/* Below 768 px the Projects column folds into a second line. */}
            {narrow && <span className="truncate text-caption text-fg-subtle">{w.builtIn ? `${projectsOf(w)}, built in` : projectsOf(w)}</span>}
          </span>
        </span>
      ),
    },
    {
      key: 'projects',
      header: 'Projects',
      hideBelow: 'md',
      cell: (w) => <span className={`block max-w-80 truncate text-sm ${w.projectIds?.length === 0 ? 'text-fg-subtle' : 'text-fg-muted'}`}>{projectsOf(w)}</span>,
    },
    {
      key: 'by',
      header: 'Created',
      hideBelow: 'lg',
      cell: (w) => (w.builtIn ? <span className="text-sm text-fg-subtle">Built in</span> : <span className="text-sm text-fg-subtle">{w.createdBy ? `${personOf(data, w.createdBy).name}, ` : ''}<RelativeTime ts={w.createdAt} /></span>),
    },
  ];
  return (
    <Panel
      title="Workspaces"
      sub={data.workspaces.length}
      icon={<Layers />}
      flush
      actions={
        canManage ? (
          <Button size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
            New workspace
          </Button>
        ) : undefined
      }
    >
      <DataTable
        frameless
        label="Workspaces"
        rows={data.workspaces}
        rowKey={(w) => w.id}
        columns={columns}
        pageSize={false}
        initialSort={{ key: 'name', dir: 'asc' }}
        empty={<EmptyState icon={<Layers />} title="No workspaces" description="All projects is always here. Create a workspace to focus on a few projects." />}
        rowActions={(w) =>
          canManage && !w.builtIn ? (
            <>
              <IconButton label={`Edit ${w.name}`} icon={<Pencil />} size="sm" onClick={() => setEditing(w)} />
              <IconButton label={`Delete ${w.name}`} icon={<Trash2 />} size="sm" onClick={() => setRemoving(w)} />
            </>
          ) : null
        }
      />
      {!canManage && <p className="border-t border-border-subtle px-4 py-3 text-sm text-fg-subtle">Admins create and change workspaces. Anyone can switch between them.</p>}
      <WorkspaceDrawer ws={editing} open={creating || !!editing} onClose={() => (setCreating(false), setEditing(null))} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? `Delete the ${removing.name} workspace?` : ''}
        description="The projects stay as they are. Anyone showing this workspace goes back to All projects."
        confirmLabel="Delete workspace"
        onConfirm={async () => {
          if (!removing) return;
          await teamsClient.deleteWorkspace(removing.id);
          if (current === removing.id) useWorkspaceChoice.getState().choose(ALL_PROJECTS_WORKSPACE_ID);
          toast.success(`${removing.name} was deleted`);
          setRemoving(null);
        }}
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

const toHandle = (s: string) => s.toLowerCase().replace(/^@/, '').replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 39);

function TeamDrawer({ data, team, open, onClose }: { data: TeamsState; team: Team | null; open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [handleTouched, setHandleTouched] = useState(false);
  const [members, setMembers] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ name?: string; handle?: string }>({});
  useEffect(() => {
    if (!open) return;
    setName(team?.name ?? '');
    setHandle(team?.handle ?? '');
    setHandleTouched(!!team);
    setMembers(team?.members ?? []);
    setError({});
  }, [open, team]);
  const people = useMemo(
    () =>
      data.people
        .filter((p) => p.login !== 'owner')
        .map((p) => ({ value: p.login, label: p.name, detail: `@${p.login}`, leading: <Avatar name={p.name} colorKey={p.login} size={20} /> })),
    [data.people],
  );
  const save = async () => {
    const h = toHandle(handle || name);
    const errs: typeof error = {};
    if (!name.trim()) errs.name = 'Give the team a name, for example Platform.';
    if (!/^[a-z0-9][a-z0-9._-]{0,38}$/.test(h)) errs.handle = 'Letters, numbers, dots, dashes or underscores, for example platform.';
    else if (data.people.some((p) => p.login === h)) errs.handle = `@${h} is someone's login. Pick another handle.`;
    else if (data.teams.some((t) => t.handle === h && t.id !== team?.id)) errs.handle = `@${h} is already a team.`;
    setError(errs);
    if (errs.name || errs.handle) return;
    setBusy(true);
    const body = { name: name.trim(), handle: h, members };
    const ok = await attempt(() => (team ? teamsClient.updateTeam(team.id, body) : teamsClient.createTeam(body)), team ? `@${h} saved` : `@${h} created`, `Mention @${h} to reach ${members.length} ${members.length === 1 ? 'person' : 'people'}.`);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Drawer
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={team ? `Edit ${team.name}` : 'New team'}
      description="Mention a team with its handle to reach everyone in it. Teams can own projects and take routed requests."
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" className="ml-auto" loading={busy} onClick={() => void save()}>
            {team ? 'Save team' : 'Create team'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 p-5">
        <Input
          label="Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!handleTouched) setHandle(toHandle(e.target.value));
          }}
          error={error.name}
          maxLength={60}
          autoFocus
        />
        <Input
          label="Handle"
          hint="What people type after @. Renaming it updates the projects this team owns."
          value={handle}
          onChange={(e) => (setHandle(e.target.value.replace(/^@/, '')), setHandleTouched(true))}
          error={error.handle}
          icon={<span className="text-sm text-fg-subtle">@</span>}
          spellCheck={false}
        />
        <CheckList label="Members" options={people} value={members} onChange={setMembers} emptyText="Nobody has signed in yet. Allow people in Members & sign-in." />
      </div>
    </Drawer>
  );
}

function TeamsPanel({ data, canManage }: { data: TeamsState; canManage: boolean }) {
  const me = useMyLogin();
  const [editing, setEditing] = useState<Team | null>(null);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState<Team | null>(null);
  const narrow = useMediaQuery(NARROW_QUERY);
  const owns = (t: Team) => data.ownership.filter((o) => o.owners.includes(`@${t.handle}`)).length;
  const columns: Column<Team>[] = [
    {
      key: 'name',
      header: 'Team',
      grow: true,
      text: (t) => `${t.name} ${t.handle}`,
      sortValue: (t) => t.name.toLowerCase(),
      cell: (t) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent">
            <Users className="size-3.5" aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-2">
              <span className="truncate font-medium text-fg">{t.name}</span>
              {me && t.members.includes(me) && (
                <Badge variant="outline" className="shrink-0">
                  You
                </Badge>
              )}
            </span>
            {/* Below 768 px the Members column folds into the handle line. */}
            <span className="block truncate text-caption text-fg-subtle">
              <span className="font-mono">@{t.handle}</span>
              {narrow && ` · ${t.members.length ? `${t.members.length} ${t.members.length === 1 ? 'person' : 'people'}` : 'Nobody yet'}`}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      hideBelow: 'md',
      // Faces first, then the count: a left-aligned cell, not a number column.
      numeric: false,
      sortValue: (t) => t.members.length,
      cell: (t) =>
        t.members.length ? (
          <span className="flex items-center gap-2">
            <AvatarStack people={t.members.map((l) => personOf(data, l))} max={4} />
            <span className="text-sm text-fg-muted num">{t.members.length}</span>
          </span>
        ) : (
          <span className="text-sm text-fg-subtle">Nobody yet</span>
        ),
    },
    { key: 'owns', header: 'Owns', type: 'number', hideBelow: 'md', sortValue: owns, cell: (t) => (owns(t) ? <span className="text-sm text-fg-muted num">{`${owns(t)} ${owns(t) === 1 ? 'project' : 'projects'}`}</span> : <span className="text-sm text-fg-subtle" aria-label="No projects">–</span>) },
  ];
  return (
    <Panel
      title="Teams"
      sub={data.teams.length || undefined}
      icon={<Users />}
      flush
      actions={
        canManage ? (
          <Button size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
            New team
          </Button>
        ) : undefined
      }
    >
      <DataTable
        frameless
        label="Teams"
        rows={data.teams}
        rowKey={(t) => t.id}
        columns={columns}
        pageSize={false}
        searchable={data.teams.length > 8 ? { placeholder: 'Search teams' } : undefined}
        initialSort={{ key: 'name', dir: 'asc' }}
        empty={
          <EmptyState
            icon={<Users />}
            title="No teams yet"
            description="A team is a handle like @platform that reaches several people at once. Teams can own projects and take routed requests."
            action={
              canManage ? (
                <Button size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
                  New team
                </Button>
              ) : undefined
            }
          />
        }
        rowActions={(t) =>
          canManage ? (
            <>
              <IconButton label={`Edit ${t.name}`} icon={<Pencil />} size="sm" onClick={() => setEditing(t)} />
              <IconButton label={`Delete ${t.name}`} icon={<Trash2 />} size="sm" onClick={() => setRemoving(t)} />
            </>
          ) : null
        }
      />
      {!canManage && <p className="border-t border-border-subtle px-4 py-3 text-sm text-fg-subtle">Admins create teams and choose who is in them.</p>}
      <TeamDrawer data={data} team={editing} open={creating || !!editing} onClose={() => (setCreating(false), setEditing(null))} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={removing ? `Delete @${removing.handle}?` : ''}
        description={
          removing && owns(removing)
            ? `It stops owning ${owns(removing)} ${owns(removing) === 1 ? 'project' : 'projects'}, and routing rules that name it are switched off. Its members keep everything else.`
            : 'Its members keep everything else. Routing rules that name it are switched off.'
        }
        confirmLabel="Delete team"
        onConfirm={async () => {
          if (!removing) return;
          await teamsClient.deleteTeam(removing.id);
          toast.success(`@${removing.handle} was deleted`);
          setRemoving(null);
        }}
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Your notifications
// ---------------------------------------------------------------------------

const URGENCY_OPTIONS: { value: Urgency; label: string }[] = [
  { value: 'low', label: 'Everything, even low urgency' },
  { value: 'normal', label: 'Normal and above' },
  { value: 'high', label: 'High and critical only' },
  { value: 'critical', label: 'Critical only' },
];

function PrefsPanel() {
  const me = useMyLogin();
  const { data, error, loading, refresh, setData } = useResource(teamsClient.prefs, 120_000);
  const [quiet, setQuiet] = useState<{ from: string; to: string }>({ from: '22:00', to: '07:00' });
  useEffect(() => {
    if (data?.quietHours) setQuiet(data.quietHours);
  }, [data?.quietHours]);
  if (!me) return null;
  const save = async (patch: Partial<Pick<MemberPrefs, 'events' | 'minUrgency' | 'quietHours'>>, done: string) => {
    try {
      setData(await teamsClient.setPrefs(patch));
      toast.success(done);
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Panel title="What reaches you" icon={<BellRing />} sub="Only yours">
      {loading && !data ? (
        <SkeletonRows rows={4} />
      ) : !data ? (
        <ErrorState error={error} onRetry={refresh} />
      ) : (
        <div className="flex flex-col gap-5">
          <fieldset className="flex flex-col gap-1">
            <legend className="mb-1.5 text-sm font-medium text-fg-muted">Tell me when</legend>
            {TEAMS_NOTIFY_EVENTS.map((ev) => (
              <label key={ev} className="flex cursor-pointer items-center gap-2.5 py-1 text-base text-fg">
                <input
                  type="checkbox"
                  className="size-4 accent-accent"
                  checked={data.events[ev]}
                  onChange={(e) => void save({ events: { ...data.events, [ev]: e.target.checked } }, e.target.checked ? 'Turned on' : 'Turned off')}
                />
                {TEAMS_NOTIFY_EVENT_LABEL[ev]}
              </label>
            ))}
          </fieldset>
          <Select
            label="Urgency"
            hint="Critical items always reach you."
            wrapperClassName="max-w-xs"
            value={data.minUrgency}
            onValueChange={(v) => void save({ minUrgency: v as Urgency }, 'Urgency saved')}
            options={URGENCY_OPTIONS}
          />
          <div className="flex flex-col gap-2">
            <label className="flex cursor-pointer items-center gap-2.5 text-base text-fg">
              <input type="checkbox" className="size-4 accent-accent" checked={!!data.quietHours} onChange={(e) => void save({ quietHours: e.target.checked ? quiet : null }, e.target.checked ? 'Quiet hours on' : 'Quiet hours off')} />
              Quiet hours: hold everything but critical for the daily digest
            </label>
            {data.quietHours && (
              <div className="flex items-end gap-2 pl-6">
                <Input type="time" size="sm" label="From" value={quiet.from} onChange={(e) => setQuiet({ ...quiet, from: e.target.value })} wrapperClassName="w-32" />
                <Input type="time" size="sm" label="To" value={quiet.to} onChange={(e) => setQuiet({ ...quiet, to: e.target.value })} wrapperClassName="w-32" />
                <Button size="sm" disabled={quiet.from === data.quietHours.from && quiet.to === data.quietHours.to} onClick={() => void save({ quietHours: quiet }, 'Quiet hours saved')}>
                  Save hours
                </Button>
              </div>
            )}
          </div>
          <p className="text-sm text-fg-subtle">These go out through the hub's notification channels when the notify module is on; everything also stays on the item in Orgus.</p>
          {error != null && <ErrorState variant="inline" error={error} onRetry={refresh} />}
        </div>
      )}
    </Panel>
  );
}

/** Settings > Teams: the organisation, workspaces, teams, and what reaches you. */
export function TeamsView() {
  const canManage = useCan('admin');
  return (
    <TeamsGate label="teams">
      {(data) => (
        <div className="flex flex-col gap-4">
          <OrgPanel data={data} canManage={canManage} />
          <WorkspacesPanel data={data} canManage={canManage} />
          <TeamsPanel data={data} canManage={canManage} />
          <PrefsPanel />
        </div>
      )}
    </TeamsGate>
  );
}
